// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export function generateId(): string {
  return crypto.randomUUID();
}

export function formatCurrency(amount: number | null, currency = 'USD'): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

// Parse a stored date. Date-only strings ('YYYY-MM-DD') are interpreted as
// LOCAL midnight rather than the JS default of UTC midnight, so they don't
// shift back a day when formatted in a negative-UTC timezone. Full ISO
// timestamps are parsed as-is.
export function parseFlexibleDate(iso: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return new Date(`${iso}T00:00:00`);
  return new Date(iso);
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return parseFlexibleDate(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatRelativeDate(iso: string | null): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

export function formatWearCount(count: number): string {
  if (count === 0) return 'Unworn';
  if (count === 1) return '1 wear';
  return `${count} wears`;
}

export function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

export function nowISO(): string {
  return new Date().toISOString();
}

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function debounce<T extends (...args: never[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

export function parseSeasonsJson(seasons: string | null): string[] {
  if (!seasons) return [];
  try {
    return JSON.parse(seasons);
  } catch {
    return [];
  }
}

export function buildQueryString(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(
    (pair): pair is [string, string] => pair[1] !== undefined && pair[1] !== '',
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries).toString();
}
